import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, BarChart2, Table, ChevronRight, Map, Building2, Phone, Sparkles, TagIcon, Info } from "lucide-react";
import { IEstablishment } from "../../interfaces/establishment";
import { getEstablishments, getEstablishmentsCount, getEstablishmentRankCounts, OpeningRankCounts, getEstablishmentTags, getUniqueNafCodes, getNafRelatedTags, NafCodeOption } from "../../interfaces/establishments";
import SignalCard from "../../components/signal/SignalCard";
import SignalTable from "../../components/signal/SignalTable";
import SignalTableSkeleton from "../../components/signal/SignalTableSkeleton";
import { Skeleton } from "../../components/ui/Skeleton";
import SignalMap from "../../components/signal/SignalMap";
import TrialPageWall from "../../components/TrialPageWall";
import MultiCityAutocomplete from "../../components/fields/MultiCityAutocomplete";
import { SIGNAL_FOLDERS, SignalFolder } from "../../interfaces/signalFolder";
import PressSignalsFeed from "../../components/signal/PressSignalsFeed";
import { FRENCH_DEPARTMENTS, FRENCH_REGIONS, REGION_TO_DEPARTMENTS } from "../../lib/frenchCities";
import { useEdgeAlignedDropdown } from "../../hooks/useEdgeAlignedDropdown";
import { Select } from "../../components/ui/Select";
import AIFilterBar from "../../components/ai/AIFilterBar";
import { AiFilters, resolveGeo } from "../../interfaces/aiFilters";
import { NB_ETAB_BUCKETS, ALL_NB_ETAB } from "../../lib/nbEtabBuckets";
import { useDebouncedEffect } from "../../hooks/useDebouncedEffect";

// Folder state saved before the switch to buckets holds per-count values ('1' … '5+') —
// map them onto the bucket that contains them so old selections stay meaningful.
const LEGACY_NB_ETAB: Record<string, string[]> = {
    '1': ['mono'],
    '2': ['petit'],
    '3': ['petit'],
    '4': ['petit'],
    '5+': ['petit', 'regional', 'national', 'grand'],
};
const normalizeNbEtab = (saved?: string[]): string[] => {
    if (!saved) return ALL_NB_ETAB;
    const selected = new Set<string>();
    saved.forEach(v => (ALL_NB_ETAB.includes(v) ? [v] : LEGACY_NB_ETAB[v] ?? []).forEach(b => selected.add(b)));
    return ALL_NB_ETAB.filter(b => selected.has(b));
};

// Multi-établissements segments: the latest opening of owners with exactly N addresses
// (owner_last_opening_rank; « 5ᵉ et + » = 5 or more).
// `key` is the field of OpeningRankCounts holding that segment's total.
const OPENING_RANKS: { value: string; label: string; key: keyof OpeningRankCounts }[] = [
    { value: '', label: 'Toutes', key: 'all' },
    { value: '2', label: '2ᵉ', key: '2' },
    { value: '3', label: '3ᵉ', key: '3' },
    { value: '4', label: '4ᵉ', key: '4' },
    { value: '5+', label: '5ᵉ et +', key: '5+' },
];
const rankCountKey = (rank: string): keyof OpeningRankCounts =>
    OPENING_RANKS.find(r => r.value === rank)?.key ?? 'all';

export default function SignalFolderDetail() {
    const { folderId } = useParams<{ folderId: string }>();
    if (folderId === "press-signals") {
        return <PressSignalsFeed />;
    }
    return <SignalFolderDetailInner />;
}

function SignalFolderDetailInner() {
    const { folderId } = useParams<{ folderId: string }>();
    const navigate = useNavigate();
    const [signals, setSignals] = useState<IEstablishment[]>([]);
    const [totalSignals, setTotalSignals] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const itemsPerPage = 10;
    const isTrialUser = localStorage.getItem("isSub") !== "true" && localStorage.getItem("isAdmin") !== "true" && !!localStorage.getItem("trialEndsAt");
    const trialPageLimit = 5;
    const [showTrialWall, setShowTrialWall] = useState(false);
    const [viewMode, setViewMode] = useState<'grid' | 'table' | 'map'>(() => {
        const savedMode = localStorage.getItem('signalViewMode');
        return (savedMode === 'table' || savedMode === 'grid' || savedMode === 'map') ? savedMode : 'table';
    });

    const isMounted = useRef(false);
    const isFirstFolderLoad = useRef(true);
    // Monotonic id per fetchSignals run: the deferred count promises resolve seconds after
    // the rows on cold filters, so a late count from a superseded fetch must not overwrite
    // the count belonging to the current filters.
    const fetchSeqRef = useRef(0);

    const loadFolderState = (id: string | undefined) => {
        try {
            const s = localStorage.getItem(`signal_folder_${id}`);
            return s ? JSON.parse(s) : null;
        } catch { return null; }
    };

    const saved = loadFolderState(folderId);

    const [page, setPage] = useState<number>(saved?.page ?? 1);
    const [minReviews, setMinReviews] = useState<string>(saved?.minReviews ?? '');
    const [locationFilter, setLocationFilter] = useState<string>(saved?.locationFilter ?? '');
    const [showFilters, _setShowFilters] = useState(true);
    const [onlyMultiEstablishments, setOnlyMultiEstablishments] = useState<boolean>(saved?.onlyMultiEstablishments ?? false);
    const [onlyFranchises, setOnlyFranchises] = useState<boolean>(saved?.onlyFranchises ?? false);
    const [minRating, setMinRating] = useState<string>(saved?.minRating ?? '');
    const [minEstablishments, setMinEstablishments] = useState<string>(saved?.minEstablishments ?? '2');
    const [contactFilter, setContactFilter] = useState<string>(saved?.contactFilter ?? '');
    const [onlinePresenceFilter, setOnlinePresenceFilter] = useState<string>(saved?.onlinePresenceFilter ?? '');

    // New filters
    const [detectionDateFilter, setDetectionDateFilter] = useState<string>(saved?.detectionDateFilter ?? '');
    const [userHasSetDateFilter, setUserHasSetDateFilter] = useState<boolean>(saved?.userHasSetDateFilter ?? false);
    const [cityFilters, setCityFilters] = useState<string[]>(saved?.cityFilters ?? []);
    const [departmentFilters, setDepartmentFilters] = useState<string[]>(saved?.departmentFilters ?? []);
    const [regionFilters, setRegionFilters] = useState<string[]>(saved?.regionFilters ?? []);
    const [founderEstablishmentsCount, setFounderEstablishmentsCount] = useState<string[]>(normalizeNbEtab(saved?.founderEstablishmentsCount));
    const [contactFilters, setContactFilters] = useState<string[]>(saved?.contactFilters ?? []);
    const [networkStatus, setNetworkStatus] = useState<string[]>(saved?.networkStatus ?? []);
    const [establishmentType, setEstablishmentType] = useState<string[]>(saved?.establishmentType ?? []);
    const [concept, setConcept] = useState<string[]>(saved?.concept ?? []);
    const [ageFilter, setAgeFilter] = useState<string>(saved?.ageFilter ?? '');
    const [phoneTypeFilters, setPhoneTypeFilters] = useState<('mobile' | 'landline')[]>(saved?.phoneTypeFilters ?? []);
    const [emailTypeFilters, setEmailTypeFilters] = useState<('owner' | 'business')[]>(saved?.emailTypeFilters ?? []);
    const [controlResultFilter, setControlResultFilter] = useState<string>(saved?.controlResultFilter ?? '');
    const [controlFollowUpFilter, setControlFollowUpFilter] = useState<string>(saved?.controlFollowUpFilter ?? '');
    // Single choice: the folder in the URL is the only one shown. A saved multi-folder selection
    // (from when « Signaux » was multi-select) is ignored on purpose.
    const [activeFolderIds, setActiveFolderIds] = useState<string[]>(folderId ? [folderId] : []);
    const [hasContactInfo, setHasContactInfo] = useState<boolean>(saved?.hasContactInfo ?? true);
    const [nafCodeFilters, setNafCodeFilters] = useState<string[]>(saved?.nafCodeFilters ?? []);
    const [companySizeFilters, setCompanySizeFilters] = useState<string[]>(saved?.companySizeFilters ?? []);
    const [legalFormFilters, setLegalFormFilters] = useState<string[]>(saved?.legalFormFilters ?? []);
    const [openingRank, setOpeningRank] = useState<string>(saved?.openingRank ?? '');
    // Every segment's total under the other filters (one cached call). Kept while a refresh is
    // in flight — dimmed rather than blanked, so the segments don't jump on each filter change.
    const [rankCounts, setRankCounts] = useState<OpeningRankCounts | null>(null);
    const [rankCountsLoading, setRankCountsLoading] = useState(false);
    // Filters the counts above were computed for: paging or switching segment reuses them
    const rankCountsRef = useRef<{ key: string; counts: OpeningRankCounts } | null>(null);

    // Dropdown states for multi-select filters
    const [showContactDropdown, setShowContactDropdown] = useState(false);
    const [_showNetworkDropdown, setShowNetworkDropdown] = useState(false);
    const [showEstablishmentDropdown, setShowEstablishmentDropdown] = useState(false);
    const [_showConceptDropdown, setShowConceptDropdown] = useState(false);
    const [showEstablishmentsCountDropdown, setShowEstablishmentsCountDropdown] = useState(false);
    const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
    const [showNafDropdown, setShowNafDropdown] = useState(false);
    const [showCompanySizeDropdown, setShowCompanySizeDropdown] = useState(false);
    const [showLegalFormDropdown, setShowLegalFormDropdown] = useState(false);
    const [departmentSearch, setDepartmentSearch] = useState('');
    const [showRegionDropdown, setShowRegionDropdown] = useState(false);
    const [regionSearch, setRegionSearch] = useState('');
    const [_conceptSearch, _setConceptSearch] = useState('');

    // Edge-aware alignment: flip each dropdown to the right edge of its trigger
    // when it would otherwise overflow past the right side of the viewport.
    const contactDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showContactDropdown);
    const networkDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(_showNetworkDropdown);
    const establishmentDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showEstablishmentDropdown);
    const conceptDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(_showConceptDropdown);
    const establishmentsCountDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showEstablishmentsCountDropdown);
    const departmentDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showDepartmentDropdown);
    const nafDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showNafDropdown);
    const companySizeDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showCompanySizeDropdown);
    const legalFormDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showLegalFormDropdown);
    const regionDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showRegionDropdown);

    const _reviewOptions = [
        { value: '', label: 'Tous' },
        { value: '<10', label: 'Moins de 10 avis' },
        { value: '10', label: '10+ avis' },
        { value: '25', label: '25+ avis' },
        { value: '50', label: '50+ avis' },
        { value: '100', label: '100+ avis' },
        { value: '200', label: '200+ avis' },
    ];

    const _ratingOptions = [
        { value: '', label: 'Toutes notes' },
        { value: '3.0', label: '3+ ⭐' },
        { value: '3.5', label: '3.5+ ⭐' },
        { value: '4.0', label: '4+ ⭐' },
        { value: '4.5', label: '4.5+ ⭐' },
    ];

    const _establishmentOptions = [
        { value: '2', label: '2+ établissements' },
        { value: '3', label: '3+ établissements' },
        { value: '5', label: '5+ établissements' },
        { value: '10', label: '10+ établissements' },
    ];

    const _contactOptions = [
        { value: '', label: 'Tous' },
        { value: 'phone', label: 'Téléphone' },
        { value: 'email', label: 'Email' },
        { value: 'both', label: 'Téléphone & Email' },
    ];

    const contactLabels: Record<string, string> = {
        'linkedin': 'LinkedIn',
        'instagram': 'Instagram',
        'email': 'Email',
        'phone': 'Téléphone',
        'website': 'Site web'
    };

    const _onlinePresenceOptions = [
        { value: '', label: 'Tous' },
        { value: 'linkedin', label: 'LinkedIn' },
        { value: 'website', label: 'Website' },
        { value: 'both', label: 'LinkedIn & Website' },
    ];

    const detectionDateOptions = [
        { value: '', label: 'Toutes les dates' },
        { value: 'last-week', label: 'Dernière semaine' },
        { value: 'last-month', label: 'Dernier mois' },
        { value: 'last-3-months', label: '3 derniers mois' },
        { value: 'last-6-months', label: '6 derniers mois' },
        { value: 'last-year', label: 'Dernière année' },
    ];

    const controlResultOptions = [
        { value: '', label: 'Tous les résultats' },
        { value: 'Très satisfaisant', label: 'Très satisfaisant' },
        { value: 'Satisfaisant', label: 'Satisfaisant' },
        { value: 'A améliorer', label: 'A améliorer' },
        { value: 'A corriger de manière urgente', label: 'A corriger en urgence' },
    ];

    const controlFollowUpOptions = [
        { value: '', label: 'Toutes les suites' },
        { value: 'Avertissement', label: 'Avertissement' },
        { value: 'Mise en demeure', label: 'Mise en demeure' },
        { value: 'Procès-verbal', label: 'Procès-verbal' },
        { value: 'Fermeture administrative', label: 'Fermeture administrative' },
    ];

    const signalTypeOptions = [
        { value: 'restaurant_opening', label: 'Ouverture restaurant' },
        { value: 'restaurant_owner_change', label: 'Changement d\'exploitant' },
        { value: 'multi_site', label: 'Multi-établissement' },
        { value: 'restaurant_closing', label: 'Fermeture restaurant' },
        { value: 'hygiene_control', label: 'Contrôle sanitaire' },
        { value: 'franchise_signing', label: 'Signature franchise' },
        { value: 'network_expansion', label: 'Expansion réseau' },
    ];

    const signalTypeLabels: Record<string, string> = {
        'restaurant_opening': 'Ouverture',
        'restaurant_owner_change': 'Changement exploitant',
        'multi_site': 'Multi-site',
        'restaurant_closing': 'Fermeture',
        'hygiene_control': 'Contrôle sanitaire',
        'franchise_signing': 'Franchise',
        'network_expansion': 'Expansion réseau',
    };

    const _regionOptions = [
        'Île-de-France', 'Auvergne-Rhône-Alpes', 'Provence-Alpes-Côte d\'Azur', 'Nouvelle-Aquitaine',
        'Occitanie', 'Hauts-de-France', 'Grand Est', 'Pays de la Loire', 'Bretagne', 'Normandie',
        'Bourgogne-Franche-Comté', 'Centre-Val de Loire', 'Corse'
    ];

    const _networkStatusOptions = [
        { value: 'independant', label: 'Indépendant' },
        { value: 'franchise', label: 'Franchise' },
        { value: 'groupe', label: 'Groupe' },
    ];

    // Dynamic tags loaded from database
    const [availableCategories, setAvailableCategories] = useState<string[]>([]);
    const [availableSubCategories, setAvailableSubCategories] = useState<string[]>([]);
    const [availableNafCodes, setAvailableNafCodes] = useState<NafCodeOption[]>([]);
    const [nafSearch, setNafSearch] = useState('');

    // Use categories as establishment types and subcategories as concepts
    const establishmentTypeOptions = availableCategories.map(cat => ({
        value: cat,
        label: cat
    }));
    
    const availableConceptOptions = availableSubCategories;

    // Load unique tags from database
    useEffect(() => {
        const loadTags = async () => {
            try {
                const tags = await getEstablishmentTags();
                setAvailableCategories(tags.categories || []);
                setAvailableSubCategories(tags.subCategories || []);
                console.log('🏷️ Loaded tags:', tags);
            } catch (error) {
                console.error('Failed to load tags:', error);
            }
        };
        loadTags();

        getUniqueNafCodes()
            .then(codes => setAvailableNafCodes(codes || []))
            .catch(err => console.error('Failed to load NAF codes:', err));
    }, []);

    // Selecting a NAF code auto-selects any category/subCategory tag that
    // actually co-occurs with it in the data (real backend search, not a static mapping)
    const handleNafToggle = (code: string, checked: boolean) => {
        setNafCodeFilters(checked ? [...nafCodeFilters, code] : nafCodeFilters.filter(c => c !== code));
        if (checked) {
            getNafRelatedTags(code)
                .then(({ categories, subCategories }) => {
                    if (categories.length) {
                        setEstablishmentType(prev => Array.from(new Set([...prev, ...categories])));
                    }
                    if (subCategories.length) {
                        setConcept(prev => Array.from(new Set([...prev, ...subCategories])));
                    }
                })
                .catch(err => console.error('Failed to load related tags for NAF', code, err));
        }
    };
    
    useEffect(() => {
        localStorage.setItem('signalViewMode', viewMode);
        
        // Auto-apply '3 derniers mois' filter when switching to map view if user hasn't manually set a date filter
        if (viewMode === 'map' && !userHasSetDateFilter) {
            setDetectionDateFilter('last-3-months');
        }
    }, [viewMode, userHasSetDateFilter]);

    // Buckets that actually narrow the result set (all-selected == no constraint, and is
    // not sent to the API either), so the table knows which dirigeant the backend matched on.
    const activeNbEtabBuckets = useMemo(
        () => (founderEstablishmentsCount.length > 0 && founderEstablishmentsCount.length < ALL_NB_ETAB.length
            ? founderEstablishmentsCount
            : []),
        [founderEstablishmentsCount]
    );

    const folder = SIGNAL_FOLDERS.find(f => f.id === folderId);
    const isHygieneControlFolder = activeFolderIds.includes('hygiene-control');
    // Multi-établissements on its own: the opening-rank chips only make sense for that folder
    const isMultiSiteFolder = activeFolderIds.length === 1 && activeFolderIds[0] === 'new-establishment';
    const isAnniversaryFolderActive = activeFolderIds.some(
        id => SIGNAL_FOLDERS.find(sf => sf.id === id)?.anniversaryOnly
    );

    // Flat list of all signal types that are currently active (derived from activeFolderIds).
    // Used to pick the most-relevant signal when rendering cards/table rows.
    const activeSignalTypes = useMemo<string[]>(() => {
        const types = activeFolderIds.flatMap(id => {
            const f = SIGNAL_FOLDERS.find(sf => sf.id === id);
            if (!f) return [];
            return f.eventTypes ?? (f.futureOnly ? ['restaurant_opening'] : []);
        });
        return [...new Set(types)];
    }, [activeFolderIds]);

    // Persist filters and page to localStorage per folder
    useEffect(() => {
        localStorage.setItem(`signal_folder_${folderId}`, JSON.stringify({
            page, minReviews, locationFilter, onlyMultiEstablishments, onlyFranchises,
            minRating, minEstablishments, contactFilter, onlinePresenceFilter,
            detectionDateFilter, userHasSetDateFilter, cityFilters, departmentFilters, regionFilters,
            founderEstablishmentsCount, contactFilters, networkStatus,
            establishmentType, concept, ageFilter, phoneTypeFilters, emailTypeFilters,
            controlResultFilter, controlFollowUpFilter, activeFolderIds,
            hasContactInfo, nafCodeFilters, companySizeFilters, legalFormFilters, openingRank,
        }));
    }, [folderId, page, minReviews, locationFilter, onlyMultiEstablishments, onlyFranchises, minRating, minEstablishments, contactFilter, onlinePresenceFilter, detectionDateFilter, userHasSetDateFilter, cityFilters, departmentFilters, regionFilters, founderEstablishmentsCount, contactFilters, networkStatus, establishmentType, concept, ageFilter, phoneTypeFilters, emailTypeFilters, controlResultFilter, controlFollowUpFilter, activeFolderIds, hasContactInfo, nafCodeFilters, companySizeFilters, legalFormFilters, openingRank]);

    // When folder changes, reload saved state for the new folder
    useEffect(() => {
        // Skip on initial mount — state is already initialized from localStorage
        if (isFirstFolderLoad.current) {
            isFirstFolderLoad.current = false;
            return;
        }
        isMounted.current = false; // skip the upcoming filter-change page reset
        const newSaved = loadFolderState(folderId);
        setPage(newSaved?.page ?? 1);
        setMinReviews(newSaved?.minReviews ?? '');
        setLocationFilter(newSaved?.locationFilter ?? '');
        setOnlyMultiEstablishments(newSaved?.onlyMultiEstablishments ?? false);
        setOnlyFranchises(newSaved?.onlyFranchises ?? false);
        setMinRating(newSaved?.minRating ?? '');
        setMinEstablishments(newSaved?.minEstablishments ?? '2');
        setContactFilter(newSaved?.contactFilter ?? '');
        setOnlinePresenceFilter(newSaved?.onlinePresenceFilter ?? '');
        setDetectionDateFilter(newSaved?.detectionDateFilter ?? '');
        setUserHasSetDateFilter(newSaved?.userHasSetDateFilter ?? false);
        setCityFilters(newSaved?.cityFilters ?? []);
        setDepartmentFilters(newSaved?.departmentFilters ?? []);
        setRegionFilters(newSaved?.regionFilters ?? []);
        setFounderEstablishmentsCount(normalizeNbEtab(newSaved?.founderEstablishmentsCount));
        setContactFilters(newSaved?.contactFilters ?? []);
        setNetworkStatus(newSaved?.networkStatus ?? []);
        setEstablishmentType(newSaved?.establishmentType ?? []);
        setConcept(newSaved?.concept ?? []);
        setAgeFilter(newSaved?.ageFilter ?? '');
        setPhoneTypeFilters(newSaved?.phoneTypeFilters ?? []);
        setEmailTypeFilters(newSaved?.emailTypeFilters ?? []);
        setControlResultFilter(newSaved?.controlResultFilter ?? '');
        setControlFollowUpFilter(newSaved?.controlFollowUpFilter ?? '');
        setActiveFolderIds(folderId ? [folderId] : []);
        setHasContactInfo(newSaved?.hasContactInfo ?? true);
        setNafCodeFilters(newSaved?.nafCodeFilters ?? []);
        setCompanySizeFilters(newSaved?.companySizeFilters ?? []);
        setLegalFormFilters(newSaved?.legalFormFilters ?? []);
        setOpeningRank(newSaved?.openingRank ?? '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [folderId]);

    // Reset page to 1 when filters change (skip on initial mount and folder change to preserve saved page)
    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
            return;
        }
        setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [minReviews, locationFilter, onlyMultiEstablishments, onlyFranchises, minRating, minEstablishments, contactFilter, onlinePresenceFilter, detectionDateFilter, cityFilters, departmentFilters, regionFilters, founderEstablishmentsCount, contactFilters, networkStatus, establishmentType, concept, ageFilter, phoneTypeFilters, emailTypeFilters, controlResultFilter, controlFollowUpFilter, activeFolderIds, hasContactInfo, nafCodeFilters, companySizeFilters, legalFormFilters, openingRank]);

    useDebouncedEffect(() => {
        if (folder) {
            fetchSignals();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [folderId, page, minReviews, locationFilter, viewMode, onlyMultiEstablishments, onlyFranchises, minRating, minEstablishments, contactFilter, onlinePresenceFilter, detectionDateFilter, cityFilters, departmentFilters, regionFilters, founderEstablishmentsCount, contactFilters, networkStatus, establishmentType, concept, ageFilter, phoneTypeFilters, emailTypeFilters, controlResultFilter, controlFollowUpFilter, activeFolderIds, hasContactInfo, nafCodeFilters, companySizeFilters, legalFormFilters, openingRank]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            // Check if click is outside all dropdowns
            if (!target.closest('.relative')) {
                setShowContactDropdown(false);
                setShowNetworkDropdown(false);
                setShowEstablishmentDropdown(false);
                setShowConceptDropdown(false);
                setShowEstablishmentsCountDropdown(false);
                setShowDepartmentDropdown(false);
                setShowRegionDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const fetchSignals = async () => {
        if (!folder) return;

        try {
            setLoading(true);

            // Filters shared across all parallel calls (everything except signal-type-specific params)
            const sharedFilters: any = {
                sortBy: "signalDate",
                order: "desc",
                hasContactInfo: hasContactInfo ? 'true' : 'false',
                hidePartial: 'true', // always hide partial-diffusion establishments (name unavailable / identical to SIRET)
            };

            if (viewMode === 'map') {
                sharedFilters.page = 1;
                sharedFilters.limit = 10000;
                sharedFilters.mapView = true;
            } else {
                sharedFilters.page = page;
                sharedFilters.limit = itemsPerPage;
            }

            if (minReviews && minReviews !== '') sharedFilters.minReviews = minReviews;
            if (locationFilter && locationFilter.trim() !== '') sharedFilters.location = locationFilter;
            if (minRating && minRating !== '') sharedFilters.minRating = minRating;
            if (onlinePresenceFilter === 'linkedin' || onlinePresenceFilter === 'both') sharedFilters.hasLinkedIn = 'true';
            if (detectionDateFilter) sharedFilters.detectionDate = detectionDateFilter;
            if (cityFilters.length > 0) sharedFilters.city = cityFilters.join(',');

            const regionDepts = regionFilters.flatMap(r => REGION_TO_DEPARTMENTS[r] ?? []);
            const allDepts = [...new Set([...departmentFilters, ...regionDepts])];
            if (allDepts.length > 0) sharedFilters.departments = allDepts.join(',');

            if (founderEstablishmentsCount.length > 0 && founderEstablishmentsCount.length < ALL_NB_ETAB.length) {
                sharedFilters.founderEstablishmentsCount = founderEstablishmentsCount.join(',');
            }
            if (contactFilters.length > 0) sharedFilters.contactFilters = contactFilters.join(',');
            if (networkStatus.length > 0) sharedFilters.networkStatus = networkStatus.join(',');
            if (establishmentType.length > 0) sharedFilters.establishmentType = establishmentType.join(',');
            if (concept.length > 0) sharedFilters.concept = concept.join(',');
            if (ageFilter) sharedFilters.ageFilter = ageFilter;
            if (phoneTypeFilters && phoneTypeFilters.length > 0) sharedFilters.phoneType = phoneTypeFilters.join(',');
            if (emailTypeFilters && emailTypeFilters.length > 0) sharedFilters.emailType = emailTypeFilters.join(',');
            if (nafCodeFilters.length > 0) sharedFilters.nafCode = nafCodeFilters.join(',');
            if (companySizeFilters.length > 0) sharedFilters.workforceRange = companySizeFilters.join(',');
            if (legalFormFilters.length > 0) sharedFilters.legalForm = legalFormFilters.join(',');
            if (isMultiSiteFolder && openingRank) sharedFilters.openingRank = openingRank;

            // One call per active folder — each keeps its own is_active / date constraints.
            const callFolders: SignalFolder[] = activeFolderIds
                .map(id => SIGNAL_FOLDERS.find(f => f.id === id))
                .filter(Boolean) as SignalFolder[];

            if (callFolders.length === 0) {
                setSignals([]);
                setTotalSignals(0);
                setLoading(false);
                return;
            }

            // Build the full filter object for a given folder's call
            const buildCallFilters = (f: SignalFolder): any => {
                const types = f.eventTypes ?? (f.futureOnly ? ['restaurant_opening'] : []);
                const is_active = types.includes('restaurant_closing') ? 'all'
                    : f.isActive !== undefined ? String(f.isActive) : 'true';
                const cf: any = { ...sharedFilters, is_active };
                if (types.length > 0) cf.signalType = types.join(',');

                if (f.anniversaryOnly) {
                    cf.anniversary = 'true';
                    // Soonest anniversary first
                    cf.order = "asc";
                } else if (f.id === 'restaurant-openings') {
                    const todayEnd = new Date(); todayEnd.setHours(23, 59, 59, 999);
                    cf.to = todayEnd.toISOString();
                } else if (f.futureOnly) {
                    const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
                    cf.from = todayStart.toISOString();
                    // Future openings: closest date first, further-out ones appear on later pages
                    cf.order = "asc";
                }
                if (f.importance && f.importance.length > 0) cf.importance = f.importance.join(',');
                if (f.id === 'hygiene-control') {
                    if (controlResultFilter) cf.controlResult = controlResultFilter;
                    if (controlFollowUpFilter) cf.controlFollowUp = controlFollowUpFilter;
                }
                return cf;
            };

            const seq = ++fetchSeqRef.current;

            if (callFolders.length === 1) {
                const filters = buildCallFilters(callFolders[0]);
                if (isMultiSiteFolder) {
                    // One aggregation gives every segment's total, the selected one being the
                    // pagination total — so no separate countOnly call here. Re-asked only when a
                    // filter other than the segment or the page changed.
                    const { openingRank: _rank, page: _p, limit: _l, mapView: _m, ...base } = filters;
                    const key = JSON.stringify(base);
                    if (rankCountsRef.current?.key === key) {
                        setTotalSignals(rankCountsRef.current.counts[rankCountKey(openingRank)]);
                    } else {
                        setRankCountsLoading(true);
                        getEstablishmentRankCounts(base)
                            .then(counts => {
                                if (fetchSeqRef.current !== seq) return;
                                rankCountsRef.current = { key, counts };
                                setRankCounts(counts);
                                setTotalSignals(counts[rankCountKey(openingRank)]);
                            })
                            .catch(() => {})
                            .finally(() => { if (fetchSeqRef.current === seq) setRankCountsLoading(false); });
                    }
                } else {
                    // Rows and count are split: deferTotal returns the rows without waiting for
                    // the expensive exact count; the parallel countOnly call fills the pagination
                    // total in when it lands (instant when the backend count cache is warm).
                    getEstablishmentsCount(filters)
                        .then(c => { if (fetchSeqRef.current === seq) setTotalSignals(c.total || 0); })
                        .catch(() => {});
                }
                const response = await getEstablishments({ ...filters, deferTotal: "true" });
                setSignals(response.establishments || []);
                // The backend still includes the total when it has it for free — use it
                // without waiting for the count call.
                if (typeof response.total === "number") setTotalSignals(response.total);
            } else {
                // Parallel calls — one per folder type; counts arrive separately (see above)
                Promise.all(callFolders.map(f => getEstablishmentsCount(buildCallFilters(f))))
                    .then(cs => {
                        if (fetchSeqRef.current === seq) {
                            setTotalSignals(cs.reduce((sum, c) => sum + (c.total || 0), 0));
                        }
                    })
                    .catch(() => {});
                const responses = await Promise.all(
                    callFolders.map(f => getEstablishments({ ...buildCallFilters(f), deferTotal: "true" }))
                );

                // Merge results, deduplicate by establishment _id
                const seenIds = new Set<string>();
                const merged: IEstablishment[] = [];
                for (const response of responses) {
                    for (const est of (response.establishments || [])) {
                        if (!seenIds.has(est._id)) {
                            seenIds.add(est._id);
                            merged.push(est);
                        }
                    }
                }

                // Re-sort merged list: most recent signal of any active type first
                // (or, when every active folder is future-only, closest upcoming date first)
                const allTypes = callFolders.flatMap(f =>
                    f.eventTypes ?? (f.futureOnly ? ['restaurant_opening'] : [])
                );
                const allFutureOnly = callFolders.every(f => f.futureOnly);
                merged.sort((a: any, b: any) => {
                    const extreme = (est: any): number => {
                        const relevant = (est.signals || []).filter((s: any) => allTypes.includes(s.type));
                        if (!relevant.length) return 0;
                        const dates = relevant.map((s: any) => new Date(s.date).getTime());
                        return allFutureOnly ? Math.min(...dates) : Math.max(...dates);
                    };
                    return allFutureOnly ? extreme(a) - extreme(b) : extreme(b) - extreme(a);
                });

                setSignals(merged);
                // Totals arrive via the parallel getEstablishmentsCount calls above —
                // deferTotal responses carry total: null, so summing them here would be wrong.
            }

            setLoading(false);
        } catch (err: any) {
            setError(err.message || "Erreur lors du chargement des signaux");
            setLoading(false);
        }
    };

    const _clearAllFilters = () => {
        setMinReviews('');
        setLocationFilter('');
        setOnlyMultiEstablishments(false);
        setOnlyFranchises(false);
        setMinRating('');
        setMinEstablishments('2');
        setContactFilter('');
        setOnlinePresenceFilter('');
        setDetectionDateFilter('');
        setCityFilters([]);
        setDepartmentFilters([]);
        setRegionFilters([]);
        setFounderEstablishmentsCount(['1', '2', '3', '4', '5+']);
        setContactFilters([]);
        setNetworkStatus([]);
        setEstablishmentType([]);
        setConcept([]);
        setAgeFilter('');
        setPhoneTypeFilters([]);
        setEmailTypeFilters([]);
        setControlResultFilter('');
        setControlFollowUpFilter('');
        setActiveFolderIds(folderId ? [folderId] : []);
        setHasContactInfo(true);
        setNafCodeFilters([]);
        setCompanySizeFilters([]);
        setLegalFormFilters([]);
        setOpeningRank('');
    };

    /**
     * Apply a filter set returned by the AI bar.
     *
     * Everything the AI did not ask for is cleared — a description describes the whole
     * search. Left alone on purpose: the folder scope (`activeFolderIds`), the view mode
     * and `hasContactInfo`, which are where the user is rather than what they are looking
     * for. `founderEstablishmentsCount` falls back to every bucket, its "no constraint" value.
     */
    const applyAiFilters = (f: AiFilters) => {
        const geo = resolveGeo(f);
        setLocationFilter('');
        setCityFilters(f.cities ?? []);
        setDepartmentFilters(geo.departments);
        setRegionFilters(geo.regions);
        setEstablishmentType(f.establishmentType ?? []);
        setConcept(f.concept ?? []);
        setNafCodeFilters(f.nafCode ?? []);
        setMinRating(f.minRating != null ? String(f.minRating) : '');
        setMinReviews(f.minReviews != null ? String(f.minReviews) : '');
        setContactFilters(f.contactFilters ?? []);
        setPhoneTypeFilters(f.phoneType ?? []);
        setEmailTypeFilters(f.emailType ?? []);
        setCompanySizeFilters(f.workforceRange ?? []);
        setLegalFormFilters(f.legalForm ?? []);
        setAgeFilter(f.ageFilter ?? '');
        setNetworkStatus([]);
        setOpeningRank('');
        setFounderEstablishmentsCount(f.nbEtablissements?.length ? f.nbEtablissements : ALL_NB_ETAB);
        setControlResultFilter(f.controlResult ?? '');
        setControlFollowUpFilter(f.controlFollowUp ?? '');
        // A description is an explicit choice about dates too, including the choice not to
        // constrain them — so the flag goes up either way and the map view stops silently
        // re-applying its 3-month default on top of what was just asked for.
        setDetectionDateFilter(f.detectionDate ?? '');
        setUserHasSetDateFilter(true);
        setPage(1);
    };

    const handleSaveToggle = (signalId: string, saved: boolean) => {
        setSignals(prev => prev.map(s => {
            if (s._id === signalId) {
                const userId = localStorage.getItem("userId") || "";
                const savedBy: string[] = (s as any).savedBy || [];
                return {
                    ...s,
                    savedBy: saved
                        ? [...savedBy, userId]
                        : savedBy.filter((id: string) => id !== userId)
                };
            }
            return s;
        }));
    };

    const _activeFilterCount = [
        minReviews, 
        locationFilter, 
        onlyMultiEstablishments, 
        onlyFranchises, 
        minRating,
        contactFilter,
        onlinePresenceFilter,
        detectionDateFilter,
        ...cityFilters,
        ...departmentFilters,
        ...regionFilters,
        // Only count founderEstablishmentsCount as active if not all selected
        (founderEstablishmentsCount.length > 0 && founderEstablishmentsCount.length < ALL_NB_ETAB.length) ? true : null,
        ...contactFilters,
        ...networkStatus,
        ...establishmentType,
        ...concept,
        ageFilter,
        phoneTypeFilters,
        emailTypeFilters,
        controlResultFilter,
        controlFollowUpFilter,
        // Count folder selection as active filter only when it differs from the default [folderId]
        (activeFolderIds.length !== 1 || activeFolderIds[0] !== folderId) ? true : null,
    ].filter(Boolean).length;

    const totalPages = Math.ceil(totalSignals / itemsPerPage);

    const goToPage = (pageNumber: number) => {
        if (isTrialUser && pageNumber > trialPageLimit) {
            setShowTrialWall(true);
            return;
        }
        setPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const nextPage = () => {
        if (page < totalPages) {
            goToPage(page + 1);
        }
    };

    const prevPage = () => {
        if (page > 1) {
            goToPage(page - 1);
        }
    };

    const getPageNumbers = () => {
        const pages = [];
        const maxPagesToShow = 5;
        
        if (totalPages <= maxPagesToShow) {
            // Show all pages if total is less than max
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(1);
            
            // Calculate range around current page
            let startPage = Math.max(2, page - 1);
            let endPage = Math.min(totalPages - 1, page + 1);
            
            // Add ellipsis if needed
            if (startPage > 2) {
                pages.push('...');
            }
            
            // Add pages around current page
            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }
            
            // Add ellipsis if needed
            if (endPage < totalPages - 1) {
                pages.push('...');
            }
            
            // Always show last page
            if (totalPages > 1) {
                pages.push(totalPages);
            }
        }
        
        return pages;
    };

    const dateLabel =
        activeSignalTypes.length > 1 ? "Date" :
        activeSignalTypes[0] === "hygiene_control" ? "Jour du contrôle" :
        folder?.isActive === false ? "Fermeture" : "Ouverture";

    if (!folder) {
        return (
            <div className="min-h-screen bg-canvas flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-xl font-semibold text-ink mb-2">Catégorie introuvable</h2>
                    <button
                        onClick={() => navigate("/signals")}
                        className="text-ink hover:underline text-sm font-medium"
                    >
                        Retour aux catégories
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-canvas">
            <div className="mx-auto max-w-7xl px-6 py-6">
                {/* Quick Start Shortcuts */}
                <div className="mb-4">
                    <h2 className="text-sm font-semibold text-black uppercase tracking-wider mb-4">Signaux</h2>
                    <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                        {SIGNAL_FOLDERS.map((f) => {
                            // Single choice: picking a folder opens its page, so the URL, the folder
                            // and its saved filters always agree.
                            const isSelected = f.id === folderId;

                            return (
                                <button
                                    key={f.id}
                                    onClick={() => {
                                        if (!isSelected) navigate(`/signals/folder/${f.id}`);
                                    }}
                                    aria-pressed={isSelected}
                                    className={`flex items-center gap-3 p-4 bg-surface rounded-xl border transition-all text-left group min-w-[240px] flex-shrink-0 ${
                                        isSelected
                                            ? 'border-ink ring-1 ring-gray-900 shadow-sm'
                                            : 'border-border hover:border-border-strong hover:shadow-sm'
                                    }`}
                                >
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                        isSelected ? 'bg-canvas' : 'bg-surface-sunken group-hover:bg-surface-sunken'
                                    }`}>
                                        <span className="text-xl">{f.icon}</span>
                                    </div>
                                    <span className={`font-medium text-sm ${
                                        isSelected ? 'text-ink' : 'text-ink'
                                    }`}>
                                        {f.title}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Page Title & View Toggle */}
                <div className="flex justify-between items-center mb-1">
                    <div className="flex items-center gap-4">
                        <div className="ml-3 flex items-center gap-2">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                            </span>
                            <span className="text-sm font-medium text-ink-muted">Données en temps réel</span>
                        </div>
                    </div>

                    {/* View Mode Toggle */}
                    <div className="flex items-center gap-2 bg-surface-sunken rounded-lg p-1">
                        <button
                            onClick={() => setViewMode('table')}
                            className={`p-2 rounded ${viewMode === 'table' ? 'bg-surface shadow-sm' : 'text-ink-muted'}`}
                            title="Vue tableau"
                        >
                            <Table className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setViewMode('map')}
                            className={`p-2 rounded ${viewMode === 'map' ? 'bg-surface shadow-sm' : 'text-ink-muted'}`}
                            title="Vue carte"
                        >
                            <Map className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* AI filter bar */}
                <AIFilterBar context="signals" onApply={applyAiFilters} className="mb-4" />

                {/* Opening rank — the main cut of the Multi-établissements folder. A segmented control,
                    not loose chips: it is one exclusive choice, and must not read as one more toggle
                    of the filter bar below. */}
                {isMultiSiteFolder && (
                    <div className="mb-3 flex items-center gap-3 overflow-x-auto scrollbar-hide">
                        <span
                            className="flex items-center gap-1 text-xs font-medium text-ink-muted whitespace-nowrap"
                            title="Dernière ouverture des gérants qui ont exactement ce nombre d'adresses, adresses fermées comprises"
                        >
                            Adresse du gérant
                            <Info className="w-3.5 h-3.5 text-ink-faint" />
                        </span>
                        <div role="radiogroup" aria-label="Adresse du gérant" className="flex items-center gap-0.5 bg-surface-sunken rounded-lg p-1">
                            {OPENING_RANKS.map(r => {
                                const selected = openingRank === r.value;
                                const count = rankCounts?.[r.key];
                                const empty = count === 0 && !selected && r.value !== '';
                                return (
                                    <button
                                        key={r.key}
                                        role="radio"
                                        aria-checked={selected}
                                        onClick={() => {
                                            setOpeningRank(r.value);
                                            // A 2ᵉ adresse from four years ago is no lead: default to recent openings,
                                            // unless the rep already chose a period.
                                            if (r.value && !userHasSetDateFilter && !detectionDateFilter) {
                                                setDetectionDateFilter('last-6-months');
                                            }
                                        }}
                                        disabled={empty}
                                        className={`h-7 px-3 text-xs font-medium rounded-md flex items-center gap-1.5 whitespace-nowrap transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed ${
                                            selected ? 'bg-surface text-ink shadow-sm' : 'text-ink-muted hover:text-ink'
                                        }`}
                                    >
                                        <span>{r.label}</span>
                                        <span className={`tabular-nums transition-opacity duration-150 ${selected ? 'text-ink-muted' : 'text-ink-faint'} ${rankCountsLoading ? 'opacity-40' : ''}`}>
                                            {count === undefined ? '–' : count.toLocaleString('fr-FR')}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        {/* Say when the period was set for them, and how to undo it */}
                        {openingRank && detectionDateFilter === 'last-6-months' && !userHasSetDateFilter && (
                            <span className="text-xs text-ink-faint whitespace-nowrap">
                                Ouvertures des 6 derniers mois ·{' '}
                                <button
                                    onClick={() => { setDetectionDateFilter(''); setUserHasSetDateFilter(true); }}
                                    className="underline underline-offset-2 hover:text-ink"
                                >
                                    toutes les dates
                                </button>
                            </span>
                        )}
                    </div>
                )}

                {/* Compact Filters Bar */}
                {showFilters && (
                    <div className="mb-4 flex items-center gap-2 flex-wrap">
                        <div className="flex items-center gap-2 flex-1 flex-wrap">
                            {/* Date */}
                            <Select
                                value={detectionDateFilter}
                                onChange={(v) => {
                                    setDetectionDateFilter(v);
                                    setUserHasSetDateFilter(true);
                                }}
                                options={detectionDateOptions}
                            />

                            {isHygieneControlFolder && (
                                <Select
                                    value={controlResultFilter}
                                    onChange={setControlResultFilter}
                                    options={controlResultOptions}
                                    className={controlResultFilter ? 'bg-danger-subtle text-danger border-danger/30 hover:border-danger/30' : ''}
                                />
                            )}

                            {isHygieneControlFolder && (
                                <Select
                                    value={controlFollowUpFilter}
                                    onChange={setControlFollowUpFilter}
                                    options={controlFollowUpOptions}
                                    className={controlFollowUpFilter ? 'bg-warning-subtle text-warning border-warning/30 hover:border-warning/30' : ''}
                                />
                            )}

                            {/* Villes */}
                            <MultiCityAutocomplete
                                values={cityFilters}
                                onChange={setCityFilters}
                            />

                            {/* Département */}
                            <div className="relative">
                                <button
                                    onClick={() => {
                                        setShowDepartmentDropdown(!showDepartmentDropdown);
                                        setShowRegionDropdown(false);
                                        setShowContactDropdown(false);
                                        setShowEstablishmentDropdown(false);
                                        setShowEstablishmentsCountDropdown(false);
                                    }}
                                    className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${
                                        departmentFilters.length > 0
                                            ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90'
                                            : 'bg-surface text-ink-muted border border-border hover:border-border-strong'
                                    }`}
                                >
                                    <span>Département</span>
                                    {departmentFilters.length > 0 && (
                                        <span className="px-1 py-0.5 bg-surface text-ink rounded text-[10px] font-bold">
                                            {departmentFilters.length}
                                        </span>
                                    )}
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                {showDepartmentDropdown && (
                                    <div ref={departmentDropdownAlign.ref} className={`absolute top-full mt-1 ${departmentDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 w-56 flex flex-col`} style={{ maxHeight: 280 }}>
                                        <div className="px-2 py-1.5 border-b border-border shrink-0">
                                            <input
                                                type="text"
                                                value={departmentSearch}
                                                onChange={(e) => setDepartmentSearch(e.target.value)}
                                                placeholder="Rechercher un département..."
                                                className="w-full text-xs px-2 py-1 border border-border rounded focus:outline-none focus:border-ink"
                                                autoFocus
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                        <div className="overflow-y-auto overflow-x-hidden ds-scrollbar py-1">
                                            {FRENCH_DEPARTMENTS.filter(d => {
                                                const q = departmentSearch.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
                                                const name = d.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
                                                return !q || d.code.toLowerCase().startsWith(q) || name.includes(q);
                                            }).map(dep => (
                                                <label key={dep.code} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={departmentFilters.includes(dep.code)}
                                                        onChange={(e) => {
                                                            setDepartmentFilters(e.target.checked
                                                                ? [...departmentFilters, dep.code]
                                                                : departmentFilters.filter(c => c !== dep.code)
                                                            );
                                                        }}
                                                        className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                                    />
                                                    <span className="text-xs text-ink-muted">{dep.label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Région */}
                            <div className="relative">
                                <button
                                    onClick={() => {
                                        setShowRegionDropdown(!showRegionDropdown);
                                        setShowDepartmentDropdown(false);
                                        setShowContactDropdown(false);
                                        setShowEstablishmentDropdown(false);
                                        setShowEstablishmentsCountDropdown(false);
                                    }}
                                    className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${
                                        regionFilters.length > 0
                                            ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90'
                                            : 'bg-surface text-ink-muted border border-border hover:border-border-strong'
                                    }`}
                                >
                                    <span>Région</span>
                                    {regionFilters.length > 0 && (
                                        <span className="px-1 py-0.5 bg-surface text-ink rounded text-[10px] font-bold">
                                            {regionFilters.length}
                                        </span>
                                    )}
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                {showRegionDropdown && (
                                    <div ref={regionDropdownAlign.ref} className={`absolute top-full mt-1 ${regionDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 w-64 flex flex-col`} style={{ maxHeight: 280 }}>
                                        <div className="px-2 py-1.5 border-b border-border shrink-0">
                                            <input
                                                type="text"
                                                value={regionSearch}
                                                onChange={(e) => setRegionSearch(e.target.value)}
                                                placeholder="Rechercher une région..."
                                                className="w-full text-xs px-2 py-1 border border-border rounded focus:outline-none focus:border-ink"
                                                autoFocus
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                        <div className="overflow-y-auto overflow-x-hidden ds-scrollbar py-1">
                                            {FRENCH_REGIONS.filter(r => {
                                                const q = regionSearch.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
                                                const name = r.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
                                                return !q || name.includes(q);
                                            }).map(region => (
                                                <label key={region} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={regionFilters.includes(region)}
                                                        onChange={(e) => {
                                                            setRegionFilters(e.target.checked
                                                                ? [...regionFilters, region]
                                                                : regionFilters.filter(r => r !== region)
                                                            );
                                                        }}
                                                        className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                                    />
                                                    <span className="text-xs text-ink-muted">{region}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Nb établissements */}
                            <div className="relative">
                                <button
                                    onClick={() => {
                                        setShowEstablishmentsCountDropdown(!showEstablishmentsCountDropdown);
                                        setShowContactDropdown(false);
                                        setShowNetworkDropdown(false);
                                        setShowEstablishmentDropdown(false);
                                        setShowConceptDropdown(false);
                                    }}
                                    className={`h-8 px-2.5 text-xs font-medium rounded-md transition-colors duration-150 flex items-center gap-1 ${
                                        founderEstablishmentsCount.length > 0 && founderEstablishmentsCount.length < ALL_NB_ETAB.length
                                            ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90'
                                            : 'bg-surface text-ink-muted border border-border hover:border-border-strong'
                                    }`}
                                >
                                    <Building2 className="w-4 h-4" />
                                    <span>{isMultiSiteFolder ? 'Taille du groupe' : "Nb d'établissement"}</span>
                                    {founderEstablishmentsCount.length > 0 && founderEstablishmentsCount.length < ALL_NB_ETAB.length && (
                                        <span className="px-1 py-0.5 bg-ink-inverse/20 text-ink-inverse rounded-sm text-[10px] font-bold">
                                            {founderEstablishmentsCount.length}
                                        </span>
                                    )}
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                {showEstablishmentsCountDropdown && (
                                    <div ref={establishmentsCountDropdownAlign.ref} className={`absolute top-full mt-1 ${establishmentsCountDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 min-w-[200px] py-1`}>
                                        {/* Option "Tous" */}
                                        <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer border-b border-border">
                                            <input
                                                type="checkbox"
                                                checked={founderEstablishmentsCount.length === ALL_NB_ETAB.length}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setFounderEstablishmentsCount(ALL_NB_ETAB);
                                                    } else {
                                                        setFounderEstablishmentsCount([]);
                                                    }
                                                }}
                                                className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                            />
                                            <span className="text-xs text-ink-muted font-medium">Tous</span>
                                        </label>
                                        
                                        {/* Options individuelles */}
                                        {NB_ETAB_BUCKETS.map(({ value, label, sub }) => (
                                            <label key={value} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={founderEstablishmentsCount.includes(value)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setFounderEstablishmentsCount([...founderEstablishmentsCount, value]);
                                                        } else {
                                                            setFounderEstablishmentsCount(founderEstablishmentsCount.filter(c => c !== value));
                                                        }
                                                    }}
                                                    className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                                />
                                                <span className="text-xs text-ink-muted">{label}</span>
                                                <span className="text-[11px] text-ink-faint">{sub}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Âge fondateur */}
                            <Select
                                value={ageFilter}
                                onChange={setAgeFilter}
                                options={[
                                    { value: "", label: "Tous les âges" },
                                    { value: "<35", label: "< 35 ans" },
                                    { value: "35-45", label: "35 – 45 ans" },
                                    { value: "45-55", label: "45 – 55 ans" },
                                    { value: "55+", label: "55+ ans" },
                                ]}
                                className={ageFilter ? 'bg-info-subtle text-info border-info/30 hover:border-info/30' : ''}
                            />

                            {/* Coordonnées */}
                            <div className="relative">
                                <button
                                    onClick={() => {
                                        setShowContactDropdown(!showContactDropdown);
                                        setShowNetworkDropdown(false);
                                        setShowEstablishmentDropdown(false);
                                        setShowConceptDropdown(false);
                                        setShowEstablishmentsCountDropdown(false);
                                    }}
                                    className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${
                                        contactFilters.length > 0 || phoneTypeFilters.length > 0 || emailTypeFilters.length > 0
                                            ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90'
                                            : 'bg-surface text-ink-muted border border-border hover:border-border-strong'
                                    }`}
                                >
                                    <Phone className="w-4 h-4" />
                                    {(contactFilters.length > 0 || phoneTypeFilters.length > 0 || emailTypeFilters.length > 0) && (
                                        <span className="px-1 py-0.5 bg-surface text-ink rounded text-[10px] font-bold">
                                            {contactFilters.length + phoneTypeFilters.length + emailTypeFilters.length}
                                        </span>
                                    )}
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                {showContactDropdown && (
                                    <div ref={contactDropdownAlign.ref} className={`absolute top-full mt-1 ${contactDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 min-w-[160px] py-1`}>
                                        <div className="px-3 py-1">
                                            <span className="text-[10px] font-semibold text-ink-faint uppercase tracking-wide">Présence en ligne</span>
                                        </div>
                                        {['linkedin', 'instagram', 'website'].map(contact => (
                                            <label key={contact} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={contactFilters.includes(contact)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setContactFilters([...contactFilters, contact]);
                                                        } else {
                                                            setContactFilters(contactFilters.filter(f => f !== contact));
                                                        }
                                                    }}
                                                    className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                                />
                                                <span className="text-xs text-ink-muted">{contactLabels[contact] || contact}</span>
                                            </label>
                                        ))}
                                        
                                        <div className="border-t border-border my-1"></div>
                                        <div className="px-3 py-1">
                                            <span className="text-[10px] font-semibold text-ink-faint uppercase tracking-wide">Type de téléphone</span>
                                        </div>
                                        <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={phoneTypeFilters.includes('mobile')}
                                                onChange={(e) => {
                                                    setPhoneTypeFilters(e.target.checked
                                                        ? [...phoneTypeFilters, 'mobile']
                                                        : phoneTypeFilters.filter(f => f !== 'mobile')
                                                    );
                                                }}
                                                className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                            />
                                            <svg className="w-4 h-4 text-ink-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                            </svg>
                                            <span className="text-xs text-ink-muted">Portable 06/07</span>
                                        </label>
                                        <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={phoneTypeFilters.includes('landline')}
                                                onChange={(e) => {
                                                    setPhoneTypeFilters(e.target.checked
                                                        ? [...phoneTypeFilters, 'landline']
                                                        : phoneTypeFilters.filter(f => f !== 'landline')
                                                    );
                                                }}
                                                className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                            />
                                            <Phone className="w-4 h-4 text-ink-muted" />
                                            <span className="text-xs text-ink-muted">Téléphone fixe</span>
                                        </label>
                                        {phoneTypeFilters.length > 0 && (
                                            <button
                                                onClick={() => setPhoneTypeFilters([])}
                                                className="w-full text-left px-3 py-1.5 text-xs text-ink-faint hover:bg-surface-sunken hover:text-ink-muted"
                                            >
                                                Réinitialiser le type
                                            </button>
                                        )}
                                        
                                        <div className="border-t border-border my-1"></div>
                                        <div className="px-3 py-1">
                                            <span className="text-[10px] font-semibold text-ink-faint uppercase tracking-wide">Type d'email</span>
                                        </div>
                                        <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={emailTypeFilters.includes('owner')}
                                                onChange={(e) => {
                                                    setEmailTypeFilters(e.target.checked
                                                        ? [...emailTypeFilters, 'owner']
                                                        : emailTypeFilters.filter(f => f !== 'owner')
                                                    );
                                                }}
                                                className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                            />
                                            <svg className="w-4 h-4 text-ink-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                            <span className="text-xs text-ink-muted">Propriétaire</span>
                                        </label>
                                        <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={emailTypeFilters.includes('business')}
                                                onChange={(e) => {
                                                    setEmailTypeFilters(e.target.checked
                                                        ? [...emailTypeFilters, 'business']
                                                        : emailTypeFilters.filter(f => f !== 'business')
                                                    );
                                                }}
                                                className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                            />
                                            <svg className="w-4 h-4 text-ink-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                            </svg>
                                            <span className="text-xs text-ink-muted">Email entreprise</span>
                                        </label>
                                        {emailTypeFilters.length > 0 && (
                                            <button
                                                onClick={() => setEmailTypeFilters([])}
                                                className="w-full text-left px-3 py-1.5 text-xs text-ink-faint hover:bg-surface-sunken hover:text-ink-muted"
                                            >
                                                Réinitialiser le type d'email
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Statut réseau */}
                            {/* <div className="relative">
                                <button
                                    onClick={() => {
                                        setShowNetworkDropdown(!_showNetworkDropdown);
                                        setShowContactDropdown(false);
                                        setShowEstablishmentDropdown(false);
                                        setShowConceptDropdown(false);
                                    }}
                                    className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${
                                        networkStatus.length > 0
                                            ? 'bg-purple-50 text-purple-700 border border-purple-300 hover:bg-purple-100'
                                            : 'bg-surface text-ink-muted border border-border hover:border-border-strong'
                                    }`}
                                >
                                    <BuildingStorefrontIcon className="w-4 h-4" />
                                    {networkStatus.length > 0 && (
                                        <span className="px-1 py-0.5 bg-purple-600 text-white rounded text-[10px] font-bold">
                                            {networkStatus.length}
                                        </span>
                                    )}
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                {_showNetworkDropdown && (
                                    <div ref={networkDropdownAlign.ref} className={`absolute top-full mt-1 ${networkDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 min-w-[180px] py-1`}>
                                        {networkStatusOptions.map(option => (
                                            <label key={option.value} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={networkStatus.includes(option.value)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setNetworkStatus([...networkStatus, option.value]);
                                                        } else {
                                                            setNetworkStatus(networkStatus.filter(s => s !== option.value));
                                                        }
                                                    }}
                                                    className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                                />
                                                <span className="text-xs text-ink-muted">{option.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div> */}

                            {/* Type établissement */}
                            <div className="relative">
                                <button
                                    onClick={() => {
                                        setShowEstablishmentDropdown(!showEstablishmentDropdown);
                                        setShowContactDropdown(false);
                                        setShowNetworkDropdown(false);
                                        setShowConceptDropdown(false);
                                    }}
                                    className={`h-8 px-2.5 text-xs font-medium rounded-md transition-colors duration-150 flex items-center gap-1 ${
                                        establishmentType.length > 0
                                            ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90'
                                            : 'bg-surface text-ink-muted border border-border hover:border-border-strong'
                                    }`}
                                >
                                    <Sparkles className="w-4 h-4" />
                                    {establishmentType.length > 0 && (
                                        <span className="px-1 py-0.5 bg-ink-inverse/20 text-ink-inverse rounded-sm text-[10px] font-bold">
                                            {establishmentType.length}
                                        </span>
                                    )}
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                {showEstablishmentDropdown && (
                                    <div ref={establishmentDropdownAlign.ref} className={`absolute top-full mt-1 ${establishmentDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 min-w-[200px] max-h-[280px] overflow-y-auto overflow-x-hidden ds-scrollbar py-1`}>
                                        {establishmentTypeOptions.map(option => (
                                            <label key={option.value} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={establishmentType.includes(option.value)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setEstablishmentType([...establishmentType, option.value]);
                                                        } else {
                                                            setEstablishmentType(establishmentType.filter(t => t !== option.value));
                                                        }
                                                    }}
                                                    className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                                />
                                                <span className="text-xs text-ink-muted">{option.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Code NAF */}
                            <div className="relative">
                                <button
                                    onClick={() => { if (showNafDropdown) setNafSearch(''); setShowNafDropdown(!showNafDropdown); setShowCompanySizeDropdown(false); setShowLegalFormDropdown(false); setShowContactDropdown(false); setShowEstablishmentDropdown(false); }}
                                    className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${nafCodeFilters.length > 0 ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90' : 'bg-surface text-ink-muted border border-border hover:border-border-strong'}`}
                                >
                                    <span>NAF</span>
                                    {nafCodeFilters.length > 0 && <span className="px-1 py-0.5 bg-surface text-ink rounded text-[10px] font-bold">{nafCodeFilters.length}</span>}
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                </button>
                                {showNafDropdown && (
                                    <div ref={nafDropdownAlign.ref} className={`absolute top-full mt-1 ${nafDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 min-w-[240px] py-1`}>
                                        <div className="px-2 py-1.5 border-b border-border">
                                            <input
                                                type="text"
                                                value={nafSearch}
                                                onChange={(e) => setNafSearch(e.target.value)}
                                                placeholder="Rechercher un code NAF..."
                                                className="w-full text-xs px-2 py-1 border border-border rounded focus:outline-none focus:border-accent"
                                                onClick={(e) => e.stopPropagation()}
                                                autoFocus
                                            />
                                        </div>
                                        <div className="max-h-[280px] overflow-y-auto overflow-x-hidden ds-scrollbar py-1">
                                            {availableNafCodes
                                                .map(({ code, description }) => ({ code, label: description ? `${code} - ${description}` : code }))
                                                .filter(({ label }) => label.toLowerCase().includes(nafSearch.toLowerCase()))
                                                .map(({ code, label }) => (
                                                    <label key={code} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                                        <input type="checkbox" checked={nafCodeFilters.includes(code)} onChange={(e) => handleNafToggle(code, e.target.checked)} className="w-3.5 h-3.5 rounded-sm border-border accent-accent" />
                                                        <span className="text-xs text-ink-muted">{label}</span>
                                                    </label>
                                                ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Taille entreprise */}
                            <div className="relative">
                                <button
                                    onClick={() => { setShowCompanySizeDropdown(!showCompanySizeDropdown); setShowNafDropdown(false); setShowLegalFormDropdown(false); setShowContactDropdown(false); setShowEstablishmentDropdown(false); }}
                                    className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${companySizeFilters.length > 0 ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90' : 'bg-surface text-ink-muted border border-border hover:border-border-strong'}`}
                                >
                                    <span>Taille</span>
                                    {companySizeFilters.length > 0 && <span className="px-1 py-0.5 bg-surface text-ink rounded text-[10px] font-bold">{companySizeFilters.length}</span>}
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                </button>
                                {showCompanySizeDropdown && (
                                    <div ref={companySizeDropdownAlign.ref} className={`absolute top-full mt-1 ${companySizeDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 w-56 py-1 max-h-80 overflow-y-auto overflow-x-hidden ds-scrollbar`}>
                                        {([
                                            { header: 'TPE', ranges: ['Non employeur', '0 salarié', '1-2 salariés', '3-5 salariés', '6-9 salariés'] },
                                            { header: 'PME', ranges: ['10-19 salariés', '20-49 salariés', '50-99 salariés', '100-199 salariés', '200-249 salariés'] },
                                            { header: 'ETI', ranges: ['250-499 salariés', '500-999 salariés', '1 000-1 999 salariés', '2 000-4 999 salariés'] },
                                            { header: 'GE',  ranges: ['5 000-9 999 salariés', '10 000 salariés et plus'] },
                                        ]).map(({ header, ranges }) => (
                                            <div key={header}>
                                                <div className="px-3 pt-2 pb-0.5">
                                                    <span className="text-[10px] font-semibold text-ink-faint uppercase tracking-wide">{header}</span>
                                                </div>
                                                {ranges.map(range => (
                                                    <label key={range} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                                        <input type="checkbox" checked={companySizeFilters.includes(range)} onChange={(e) => setCompanySizeFilters(e.target.checked ? [...companySizeFilters, range] : companySizeFilters.filter(s => s !== range))} className="w-3.5 h-3.5 rounded-sm border-border accent-accent" />
                                                        <span className="text-xs text-ink-muted">{range}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Forme juridique */}
                            <div className="relative">
                                <button
                                    onClick={() => { setShowLegalFormDropdown(!showLegalFormDropdown); setShowNafDropdown(false); setShowCompanySizeDropdown(false); setShowContactDropdown(false); setShowEstablishmentDropdown(false); }}
                                    className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${legalFormFilters.length > 0 ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90' : 'bg-surface text-ink-muted border border-border hover:border-border-strong'}`}
                                >
                                    <span>Forme juridique</span>
                                    {legalFormFilters.length > 0 && <span className="px-1 py-0.5 bg-surface text-ink rounded text-[10px] font-bold">{legalFormFilters.length}</span>}
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                </button>
                                {showLegalFormDropdown && (
                                    <div ref={legalFormDropdownAlign.ref} className={`absolute top-full mt-1 ${legalFormDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 whitespace-nowrap py-1`}>
                                        {['SAS', 'SASU', 'SARL', 'EURL', 'SA', 'Entrepreneur individuel'].map(form => (
                                            <label key={form} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                                <input type="checkbox" checked={legalFormFilters.includes(form)} onChange={(e) => setLegalFormFilters(e.target.checked ? [...legalFormFilters, form] : legalFormFilters.filter(f => f !== form))} className="w-3.5 h-3.5 rounded-sm border-border accent-accent" />
                                                <span className="text-xs text-ink-muted">{form}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Concept */}
                            {availableConceptOptions.length > 0 && (
                                <div className="relative">
                                    <button
                                        onClick={() => {
                                            if (_showConceptDropdown) _setConceptSearch('');
                                            setShowConceptDropdown(!_showConceptDropdown);
                                            setShowContactDropdown(false);
                                            setShowNetworkDropdown(false);
                                            setShowEstablishmentDropdown(false);
                                        }}
                                        className={`h-8 px-2.5 text-xs font-medium rounded-md transition-colors duration-150 flex items-center gap-1 ${
                                            concept.length > 0
                                                ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90'
                                                : 'bg-surface text-ink-muted border border-border hover:border-border-strong'
                                        }`}
                                    >
                                        <TagIcon className={`w-4 h-4 ${concept.length > 0 ? 'text-ink-inverse' : 'text-ink-muted'}`} />
                                        {concept.length > 0 && (
                                            <span className="px-1 py-0.5 bg-ink-inverse/20 text-ink-inverse rounded-sm text-[10px] font-bold">
                                                {concept.length}
                                            </span>
                                        )}
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                    {_showConceptDropdown && (
                                        <div ref={conceptDropdownAlign.ref} className={`absolute top-full mt-1 ${conceptDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 min-w-[180px]`}>
                                            <div className="px-2 py-1.5 border-b border-border">
                                                <input
                                                    type="text"
                                                    value={_conceptSearch}
                                                    onChange={(e) => _setConceptSearch(e.target.value)}
                                                    placeholder="Rechercher..."
                                                    className="w-full text-xs px-2 py-1 border border-border rounded focus:outline-none focus:border-accent"
                                                    onClick={(e) => e.stopPropagation()}
                                                    autoFocus
                                                />
                                            </div>
                                            <div className="max-h-[240px] overflow-y-auto overflow-x-hidden ds-scrollbar py-1">
                                                {availableConceptOptions.filter(c => c.toLowerCase().includes(_conceptSearch.toLowerCase())).map(conceptOption => (
                                                    <label key={conceptOption} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={concept.includes(conceptOption)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setConcept([...concept, conceptOption]);
                                                                } else {
                                                                    setConcept(concept.filter(c => c !== conceptOption));
                                                                }
                                                            }}
                                                            className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                                        />
                                                        <span className="text-xs text-ink-muted">{conceptOption}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Has Contact Info Toggle */}
                            <button
                                onClick={() => setHasContactInfo(!hasContactInfo)}
                                className={`h-8 px-2.5 text-xs font-medium rounded-md transition-colors duration-150 flex items-center gap-1.5 ${
                                    hasContactInfo
                                        ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90'
                                        : 'bg-surface text-ink-muted border border-border hover:border-border-strong'
                                }`}
                                title="Masquer les établissements sans coordonnées"
                            >
                                <Phone className="w-3.5 h-3.5" />
                                <span>Avec contact</span>
                            </button>

                        </div>

                    </div>
                )}

                {/* Signals Content */}
                {loading && page === 1 ? (
                    viewMode === 'map' ? (
                        <Skeleton className="h-[600px] w-full !rounded-xl border border-border" />
                    ) : (
                        <SignalTableSkeleton rows={itemsPerPage} dateLabel={dateLabel} />
                    )
                ) : error ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-danger text-sm">{error}</div>
                    </div>
                ) : signals.length === 0 ? (
                    <div className="text-center py-20 bg-surface rounded-xl border border-border shadow-sm">
                        <BarChart2 className="w-16 h-16 text-ink-faint mx-auto mb-4" />
                        <h3 className="text-lg font-bold text-ink mb-2">
                            Aucun signal trouvé
                        </h3>
                        <p className="text-sm text-ink-faint max-w-md mx-auto">
                            Aucun signal ne correspond aux critères de cette catégorie pour le moment. Revenez bientôt !
                        </p>
                    </div>
                ) : (
                    <>
                        {viewMode === 'grid' ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                {signals.map(signal => (
                                    <SignalCard
                                        key={signal._id}
                                        signal={signal}
                                        onSaveToggle={handleSaveToggle}
                                        activeSignalTypes={activeSignalTypes}
                                    />
                                ))}
                            </div>
                        ) : viewMode === 'table' ? (
                            <SignalTable
                                signals={signals}
                                onSaveToggle={handleSaveToggle}
                                dateLabel={dateLabel}
                                activeSignalTypes={activeSignalTypes}
                                showAnniversary={isAnniversaryFolderActive}
                                phoneTypeFilter={phoneTypeFilters}
                                emailTypeFilter={emailTypeFilters}
                                nbEtabBuckets={activeNbEtabBuckets}
                                countClosed={activeSignalTypes.length === 1 && activeSignalTypes[0] === "restaurant_closing"}
                                onOwnersEnriched={fetchSignals}
                            />
                        ) : (
                            <SignalMap
                                signals={signals}
                                onSaveToggle={handleSaveToggle}
                            />
                        )}

                        {/* Pagination (hide for map view) */}
                        {viewMode !== 'map' && totalPages > 1 && (
                            <div className="mt-4 flex items-center justify-between bg-surface rounded-xl border border-border px-4 py-3">
                                {/* Results Info */}
                                <div className="text-xs text-ink-faint">
                                    <span className="font-medium text-ink">{((page - 1) * itemsPerPage) + 1}</span>-<span className="font-medium text-ink">{Math.min(page * itemsPerPage, totalSignals)}</span>
                                </div>

                                {/* Pagination Controls */}
                                <div className="flex items-center gap-1">
                                    {/* Previous Button */}
                                    <button
                                        onClick={prevPage}
                                        disabled={page === 1}
                                        className="p-1.5 rounded bg-surface-sunken hover:bg-border disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        title="Page précédente"
                                    >
                                        <ChevronLeft className="w-4 h-4 text-ink-muted" />
                                    </button>

                                    {/* Page Numbers */}
                                    <div className="flex items-center gap-0.5">
                                        {getPageNumbers().map((pageNum, index) => (
                                            pageNum === '...' ? (
                                                <span key={`ellipsis-${index}`} className="px-2 text-ink-faint">...</span>
                                            ) : (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => goToPage(pageNum as number)}
                                                    className={`min-w-[32px] h-8 px-2 rounded font-medium text-xs transition-colors ${
                                                        page === pageNum
                                                            ? 'bg-black text-white'
                                                            : 'bg-surface-sunken text-ink-muted hover:bg-border'
                                                    }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            )
                                        ))}
                                    </div>

                                    {/* Next Button */}
                                    <button
                                        onClick={nextPage}
                                        disabled={page === totalPages}
                                        className="p-1.5 rounded bg-surface-sunken hover:bg-border disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        title="Page suivante"
                                    >
                                        <ChevronRight className="w-4 h-4 text-ink-muted" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        {showTrialWall && <TrialPageWall onClose={() => setShowTrialWall(false)} />}
        </div>
    );
}
